/**
 * Client-side validation for schedule parameter values.
 *
 * Validates required fields, integer bounds, dynamic offset ranges,
 * and static date range ordering.
 *
 * Requirements: 1.5, 1.6, 6.1, 6.2, 6.3, 6.6, 6.7
 */
import type { ParameterDef } from "@/types";
import type { ScheduleParameterValues } from "@/types/schedule";

/**
 * Validate schedule parameter values against their definitions.
 *
 * @param params - Array of parameter definitions from the plugin metadata
 * @param values - Current form values keyed by parameter name
 * @returns Record where keys are parameter names and values are error messages (empty record = valid)
 */
export function validateScheduleParams(
  params: ParameterDef[],
  values: ScheduleParameterValues,
): Record<string, string> {
  const errors: Record<string, string> = {};

  for (const param of params) {
    const entry = values[param.name];

    // Required field validation
    if (param.required) {
      if (!entry || isEmptyValue(param.type, entry.value, entry.mode)) {
        errors[param.name] = "This field is required";
        continue; // Skip further validation if required and empty
      }
    }

    // Skip further validation if no entry or no value set
    if (!entry) continue;

    // param_integer min/max bounds validation
    if (param.type === "param_integer" && entry.value != null && entry.value !== "") {
      const numValue = Number(entry.value);
      if (!Number.isInteger(numValue)) {
        errors[param.name] = "Value must be a whole number";
      } else if (param.min != null && param.max != null && (numValue < param.min || numValue > param.max)) {
        errors[param.name] = `Value must be between ${param.min} and ${param.max}`;
      } else if (param.min != null && numValue < param.min) {
        errors[param.name] = `Value must be at least ${param.min}`;
      } else if (param.max != null && numValue > param.max) {
        errors[param.name] = `Value must be at most ${param.max}`;
      }
    }

    // Date range validation
    if (param.type === "param_date_range") {
      if (entry.mode === "dynamic") {
        // Dynamic offset validation: integer, 1–365
        const offset = entry.offset;
        if (offset) {
          const amount = offset.amount;
          if (amount == null || !Number.isInteger(amount) || amount < 1 || amount > 365) {
            errors[param.name] = "Offset must be between 1 and 365";
          }
        } else if (param.required) {
          // offset is required for dynamic mode when field is required
          errors[param.name] = "This field is required";
        }
      } else if (entry.mode === "static") {
        // Static date range: start < end
        const rangeValue = entry.value as { from?: string; to?: string } | null;
        if (rangeValue && rangeValue.from && rangeValue.to) {
          if (rangeValue.from >= rangeValue.to) {
            errors[param.name] = "Start date must be before end date";
          }
        }
      }
    }
  }

  return errors;
}

/**
 * Determine whether a parameter value is considered empty based on its type.
 */
function isEmptyValue(paramType: string, value: unknown, mode?: string): boolean {
  if (value === null || value === undefined) return true;

  if (paramType === "param_date_range") {
    if (mode === "dynamic") {
      // For dynamic mode, check if offset is missing — handled by caller via entry.offset
      return false;
    }
    // For static mode, both from and to must be present
    const rangeValue = value as { from?: string; to?: string } | null;
    if (!rangeValue) return true;
    return !rangeValue.from || !rangeValue.to;
  }

  if (paramType === "param_multi_select") {
    return Array.isArray(value) && value.length === 0;
  }

  if (paramType === "param_boolean") {
    // Boolean fields always have a value (true/false), so never empty
    return false;
  }

  if (typeof value === "string") return value.trim() === "";

  // For select/search types, null is empty (handled above)
  // For objects (like search results with id/name), check if it's a valid selection
  if (typeof value === "object" && value !== null) {
    // Object values (e.g. {id, name} for search params) are considered non-empty
    return false;
  }

  return false;
}
